import { useState, useEffect, useRef } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import type { Swiper as SwiperInstance } from "swiper";
import { FreeMode } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "./NewsList.scss";
import { useQuery } from "@tanstack/react-query";
import { fetchGlobalData } from "../../../utils/utils.tsx";
import { NewsCategory_Array } from "../../../types/api/news-category.ts";
import { Article_Array } from "../../../types/api/article.ts";
import { useTranslation } from "react-i18next";

const NewsList = ({ locale }: { locale?: string }) => {
  const { t } = useTranslation();
  const [activeCategory, setActiveCategory] = useState<number | null>(null);
  const tabsSwiperRef = useRef<{ swiper: SwiperInstance } | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [searchApplied, setSearchApplied] = useState<string>("");
  const pageSize = 6; // Number of articles per page
  const categoriesRef = useRef<HTMLDivElement>(null);
  const localeParam = locale ? `&locale=${locale}` : "";
  const newsBasePath = locale ? `/${locale}/news` : "/news";

  // Fetch categories
  const {
    data: categories,
    error: categoriesError,
    isLoading: isCategoriesLoading,
    isError: isCategoriesError,
  } = useQuery<NewsCategory_Array>({
    queryKey: [
      `/api/news-categories?customPopulate=nested&sort=order:asc${localeParam}`,
    ],
    queryFn: ({ queryKey }) => fetchGlobalData(queryKey[0] as string),
  });

  // Set the first category as active by default when data is loaded
  useEffect(() => {
    if (categories && categories.data.length > 0 && activeCategory === null) {
      setActiveCategory(categories.data[0].id);
    }
  }, [categories, activeCategory]);

  // Build query string for API
  const buildQueryString = () => {
    let queryString = `/api/articles?customPopulate=nested&pagination[withCount]=true&pagination[pageSize]=${pageSize}&pagination[page]=${currentPage}&sort=date:desc`;

    if (locale) {
      queryString += `&locale=${locale}`;
    }

    // Add category filter if active
    if (activeCategory) {
      queryString += `&filters[news_categories][id][$eq]=${activeCategory}`;
    }

    // Add search filter if applied
    if (searchApplied) {
      queryString += `&filters[$or][0][name][$containsi]=${encodeURIComponent(searchApplied)}`;
      queryString += `&filters[$or][1][description][$containsi]=${encodeURIComponent(searchApplied)}`;
    }

    return queryString;
  };

  // Fetch articles based on active category, search, and pagination
  const {
    data: articlesResponse,
    error: articlesError,
    isLoading: isArticlesLoading,
    isError: isArticlesError,
  } = useQuery<Article_Array>({
    queryKey: [buildQueryString()],
    queryFn: ({ queryKey }) => fetchGlobalData(queryKey[0] as string),
    enabled: activeCategory !== null,
  });

  // Handle category click
  const handleCategoryClick = (
    categoryId: number,
    e: React.MouseEvent<HTMLAnchorElement>,
  ) => {
    e.preventDefault();
    setActiveCategory(categoryId);
    setCurrentPage(1); // Reset to first page when changing category
  };

  // Handle search input
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  // Apply search filter
  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      applySearch();
    }
  };

  const applySearch = () => {
    setSearchApplied(searchQuery);
    setCurrentPage(1); // Reset to first page when searching
  };

  // Clear search
  const clearSearch = () => {
    setSearchQuery("");
    setSearchApplied("");
    setCurrentPage(1);
  };

  // Handle pagination
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    if (categoriesRef.current) {
      const rect = categoriesRef.current.getBoundingClientRect();
      const scrollTop =
        window.pageYOffset || document.documentElement.scrollTop;
      window.scrollTo({
        top: scrollTop + rect.top - 50,
        behavior: "auto",
      });
    }
  };

  // Generate pagination buttons
  const renderPaginationButtons = () => {
    if (!articlesResponse?.meta.pagination) return null;

    const { page, pageCount } = articlesResponse.meta.pagination;
    const buttons = [];

    // Always show first page
    buttons.push(
      <button
        key={1}
        className={`paginator-page ${page === 1 ? "active" : ""}`}
        onClick={() => handlePageChange(1)}
      >
        1
      </button>,
    );

    // Logic for showing pagination with ellipsis
    if (pageCount <= 5) {
      // Show all pages if 5 or fewer
      for (let i = 2; i <= pageCount; i++) {
        buttons.push(
          <button
            key={i}
            className={`paginator-page ${page === i ? "active" : ""}`}
            onClick={() => handlePageChange(i)}
          >
            {i}
          </button>,
        );
      }
    } else {
      // Show current page with neighbors and ellipsis
      if (page > 2) {
        buttons.push(
          <button key="skip-start" className="paginator-page skip">
            ...
          </button>,
        );
      }

      // Pages around current
      for (
        let i = Math.max(2, page - 1);
        i <= Math.min(pageCount - 1, page + 1);
        i++
      ) {
        if (i !== 1 && i !== pageCount) {
          buttons.push(
            <button
              key={i}
              className={`paginator-page ${page === i ? "active" : ""}`}
              onClick={() => handlePageChange(i)}
            >
              {i}
            </button>,
          );
        }
      }

      if (page < pageCount - 1) {
        buttons.push(
          <button key="skip-end" className="paginator-page skip">
            ...
          </button>,
        );
      }

      // Always show last page
      if (pageCount > 1) {
        buttons.push(
          <button
            key={pageCount}
            className={`paginator-page ${page === pageCount ? "active" : ""}`}
            onClick={() => handlePageChange(pageCount)}
          >
            {pageCount}
          </button>,
        );
      }
    }

    return buttons;
  };

  // Loading states
  if (isCategoriesLoading) return <div className="loading-block"></div>;
  if (isCategoriesError) return <div>Error: {categoriesError.message}</div>;
  if (!categories) return <div>Global error: Categories not available</div>;

  return (
    <section className="box-new-list">
      <div className="wrapper">
        <div className="new-list-categories" ref={categoriesRef}>
          <Swiper
            ref={tabsSwiperRef}
            modules={[FreeMode]}
            freeMode={true}
            slidesPerView="auto"
            spaceBetween={10}
            touchRatio={1}
            className="tabs-nav"
          >
            {categories.data.map((category) => (
              <SwiperSlide
                style={{ width: "auto" }}
                key={category.id}
                className={`tabs-nav__item ${activeCategory === category.id ? "active" : ""}`}
              >
                <div className="tabs-nav__item">
                  <a
                    href={`#${category.id}`}
                    data-id={category.id}
                    onClick={(e) => handleCategoryClick(category.id, e)}
                  >
                    {category.name}
                  </a>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
        <div className="news-search">
          <input
            placeholder={t("Type here...")}
            value={searchQuery}
            onChange={handleSearchChange}
            onKeyDown={handleSearchKeyDown}
          />
          <button
            className="search-button"
            onClick={applySearch}
            aria-label={t("Search")}
          >
            <img src="/img/search.svg" alt={t("Search")} />
          </button>
          {searchApplied && (
            <button
              className="clear-search"
              onClick={clearSearch}
              aria-label="Clear search"
            >
              ×
            </button>
          )}
        </div>

        {searchApplied && (
          <div className="search-indicator">
            {t("Search results for")}: <span>"{searchApplied}"</span>
            <button onClick={clearSearch} className="clear-search-text">
              {t("Clear")}
            </button>
          </div>
        )}

        {isArticlesLoading ? (
          <div className="loading-block"></div>
        ) : isArticlesError ? (
          <div>Error loading articles: {articlesError.message}</div>
        ) : (
          <>
            <div className="news-head">
              <span className="news-count">
                {articlesResponse?.meta.pagination.total || 0}
              </span>{" "}
              {t("results")}
            </div>
            <div className="news-list">
              {articlesResponse?.data.length === 0 ? (
                <div className="no-articles">
                  {t("No articles found in this category")}
                </div>
              ) : (
                articlesResponse?.data.map((article) => (
                  <div className="news-list__item" key={article.id}>
                    <a
                      className="news-list__img"
                      target="_blank"
                      href={`${newsBasePath}/${article.slug}`}
                    >
                      <img
                        src={article.image?.url}
                        alt={article.name}
                        width={"332"}
                      />
                    </a>
                    <div className="news-list__cont">
                      <div className="news-list__name">
                        <a
                          href={`${newsBasePath}/${article.slug}`}
                          target="_blank"
                        >
                          {article.name}
                        </a>
                      </div>
                      <div className="news-list__descr">
                        <div className="news-list__descr-text">
                          {article.date} <br />
                          <br />
                          {article.description}
                        </div>
                      </div>
                      <div className="news-list__read-more">
                        <a
                          href={`${newsBasePath}/${article.slug}`}
                          target="_blank"
                        >
                          {t("Read more")} <span></span>
                        </a>
                      </div>
                    </div>
                    <a
                      className="news-list__link"
                      target="_blank"
                      href={`${newsBasePath}/${article.slug}`}
                    ></a>
                  </div>
                ))
              )}
            </div>

            {articlesResponse &&
              articlesResponse.meta.pagination.pageCount > 1 && (
                <div className="paginator">
                  <div className="paginator-wrapper">
                    <button
                      className="paginator-previous"
                      onClick={() =>
                        handlePageChange(Math.max(1, currentPage - 1))
                      }
                      disabled={currentPage === 1}
                    >
                      left
                    </button>
                    <div className="paginator-list">
                      {renderPaginationButtons()}
                    </div>
                    <button
                      className="paginator-next"
                      onClick={() =>
                        handlePageChange(
                          Math.min(
                            articlesResponse.meta.pagination.pageCount,
                            currentPage + 1,
                          ),
                        )
                      }
                      disabled={
                        currentPage ===
                        articlesResponse.meta.pagination.pageCount
                      }
                    >
                      right
                    </button>
                  </div>
                </div>
              )}
          </>
        )}
      </div>
    </section>
  );
};

export default NewsList;
